<template>
  <ListItem
    class="single-thread"
    :class="rowClass"
  >
    <div class="thread-heading">
      <div class="title-row">
        <span
          ref="thread-title"
          class="title"
        >
          {{ threadData.title }}
        </span>
        <Tooltip content="Resolved!">
          <i
            v-if="threadData.resolved"
            class="fas fa-check resolve-icon"
          />
        </Tooltip>
      </div>
      <div class="description">
        <div class="author">
          <span>{{ author.name }}</span>, {{ postDate }}
        </div>
        <div class="tags">
          <TagBar
            :tags="threadData.tags"
            @forumTagSelection="onForumTagSelection"
          />
        </div>
      </div>
    </div>
    <div class="thread-reply">
      {{ postReplies }}
    </div>
    <div class="thread-like">
      {{ postLikes }}
    </div>
    <div class="thread-last-updated">
      <template v-if="replier.name">
        <p class="replier">
          by {{ replier.name }}
        </p>
        <p>
          {{ updatedFromNow(threadData.lastReplyAt) }}
        </p>
      </template>
    </div>
  </ListItem>
</template>

<script>
import TagBar from './TagBar'
import moment from 'moment'
import _ from 'lodash'
import { mapState } from 'vuex'

export default {
  components: {
    TagBar
  },

  props: {
    threadData: Object,
    pageViewSwitch: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      author: {
        name: '',
        email: '',
        id: null,
        imageUrl: null
      },
      replier: {
        name: '',
        email: '',
        id: null,
        imageUrl: null
      },
      postDate: null
    }
  },

  computed: {
    ...mapState('forum', {
      likesForPage: 'likesForPage',
      searchText: 'searchText',
      searchResults: 'searchResults'
    }),

    postReplies () {
      return this.threadData.numReplies > 0 ? this.threadData.numReplies : ''
    },

    postLikes () {
      const result = this.likesForPage.find(item => item['component_id'] === this.threadData.id)
      return result ? result['total_likes'] : ''
    },

    isTargeted () {
      return this.searchText && _.has(this.searchResults, [this.threadData.id, 'matches']) && this.searchResults[this.threadData.id].matches.length > 0
    },

    rowClass () {
      return this.threadData.sticky ? 'sticky' : ''
    }
  },

  watch: {
    'threadData.author' () {
      this.setThreadInfo()
    },

    pageViewSwitch () {
      this.updateHighlight()
    }
  },

  created () {
    this.setThreadInfo()
  },

  mounted () {
    this.updateHighlight()
  },

  methods: {
    /**
     * Set the data for the thread.
     */
    setThreadInfo () {
      // Set the author data.
      this.$set(this.author, 'name', this.threadData.author.name)
      this.$set(this.author, 'imageUrl', this.threadData.author.imageUrl)

      // Set the replier data.
      const lastReplier = this.threadData.lastReplier || {}
      this.$set(this.replier, 'name', lastReplier.name)
      this.$set(this.replier, 'imageUrl', lastReplier.imageUrl)

      // Set the creation date data.
      this.postDate = moment(this.threadData.createdAt).format('MMM Do, YYYY')
    },

    /**
     * Transform time to updated from now
     */
    updatedFromNow (lastReply) {
      return moment(lastReply).fromNow()
    },

    onForumTagSelection (tag) {
      const tags = (this.$route.query && this.$route.query.tags && this.$route.query.tags.split(',')) || []

      if (!tags.includes(tag)) {
        tags.push(tag)
      }

      this.$router.push({ ...this.$route, query: { ...this.$route.query, tags: tags.join(',') } })
    },

    /**
     * Update the Highlight for thread
     */
    updateHighlight () {
      this.removeHighlight()
      if (this.isTargeted) {
        const originTitle = this.threadData.title
        let previousPosition = 0
        let updatedTitle = ''
        this.searchResults[this.threadData.id].matches[0].indices.forEach(index => {
          updatedTitle += originTitle.slice(previousPosition, index[0]) +
          '<span class="title-highlight">' + originTitle.slice(index[0], index[1] + 1) + '</span>'
          previousPosition = index[1] + 1
        })
        updatedTitle += originTitle.slice(previousPosition, originTitle.length)
        const element = this.$refs['thread-title']
        this.$set(element, 'innerHTML', updatedTitle)
      }
    },

    /**
     * remove the current highlight setting for the thread
     * i.e. remove the <span> tag in title
     */
    removeHighlight () {
      const element = this.$refs['thread-title']
      const regex = /(<([^>]+)>)/ig
      this.$set(element, 'innerHTML', element.innerHTML.replace(regex, ''))
    }
  }
}
</script>

<style scoped>
.single-thread {
  padding: 8px 16px;
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  cursor: pointer;
  min-height: 69px;
}

.single-thread:hover {
  background-color: var(--color-green-lighter);
}

.single-thread.sticky {
  background-color: rgba(var(--color-yellow-light-rgb), .6);
}

.thread-heading {
  display: flex;
  flex-direction: column;
  align-items: top;
  width: var(--title-width);
}

.thread-sticky,
.thread-reply,
.thread-like {
  margin-right: var(--col-margin);
  width: var(--col-width);
  text-align: center;
  color: var(--color-text-light);
}

.thread-last-updated {
  width: var(--last-reply-width);
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
}

.thread-heading .title {
  font-size: 14px;
  font-weight: bold;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.thread-heading .description {
  padding: 0;
  display: flex;
  flex-direction: row;
  align-items: center;
  width: 100%;
  height: 26px;
}

.thread-heading .description div {
  font-size: 12px;
  font-weight: normal;
}

.thread-heading .description .author span {
  font-style: italic;
}

.thread-heading .description .tags {
  padding-left: 16px;
}

.thread-heading .description .tags,
.thread-last-updated .replier {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.resolve-icon {
  color: var(--color-success);
  font-size: 14px;
  margin-left: 5px;
}

.title-row {
  display: flex;
  flex-direction: row;
  align-items: center;
}
</style>
